import React, { useState } from "react";
import { ApertureEye } from "@/components/ApertureEye";
import { Send, Loader2 } from "lucide-react";
import { unlockAudio } from "@/lib/tts";

/**
 * Single-step onboarding: just ask for the user's own name.
 * The assistant is always called "mEYEtrainer" — no avatar picker on first run.
 * (Users can still rename the assistant + pick an avatar later via Tools > Settings.)
 */
export const OnboardingDialog = ({ onSubmit }) => {
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e?.preventDefault();
        unlockAudio(); // must fire inside the touch gesture on iOS
        const clean = name.trim();
        if (!clean || saving) return;
        setSaving(true);
        try {
            await onSubmit({ userName: clean });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[110] flex items-center justify-center px-6 bg-black overflow-y-auto"
            data-testid="onboarding-dialog"
        >
            <div className="absolute inset-0 bg-[#050505]" />
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="ss-blob ss-blob-1" />
                <div className="ss-blob ss-blob-2" />
            </div>
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at center, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.5) 40%, rgba(0,0,0,0.1) 80%, transparent 100%)",
                }}
            />

            <div className="relative z-10 w-full max-w-md fade-up py-8">
                <div className="flex justify-center mb-6">
                    <ApertureEye size={64} className="opacity-95" />
                </div>

                <div className="text-center mb-6">
                    <p className="text-[10px] uppercase tracking-[0.4em] text-meye-cyan font-mono mb-2">
                        mEYEtrainer &gt; initializing
                    </p>
                    <h1 className="font-brand text-3xl sm:text-4xl font-bold text-zinc-100 tracking-tight mb-3">
                        first things first —
                    </h1>
                    <p className="font-brand text-lg text-zinc-400">
                        what should I call you, trainer?
                    </p>
                </div>

                <form onSubmit={submit} className="flex items-center gap-2">
                    <input
                        autoFocus
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="your name..."
                        maxLength={40}
                        className="flex-1 bg-black/60 border border-meye-fuchsia/40 rounded-lg px-4 py-3 text-base text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-meye-fuchsia focus:ring-2 focus:ring-meye-fuchsia/20 transition-colors font-brand"
                        data-testid="onboarding-input"
                    />
                    <button
                        type="submit"
                        disabled={!name.trim() || saving}
                        className="p-3 rounded-lg bg-meye-fuchsia hover:bg-fuchsia-400 text-black disabled:opacity-40 transition-colors flex items-center justify-center min-w-[48px]"
                        data-testid="onboarding-submit"
                        aria-label="Enter mEYEtrainer"
                    >
                        {saving ? (
                            <Loader2 size={18} className="animate-spin" />
                        ) : (
                            <Send size={16} strokeWidth={2.5} />
                        )}
                    </button>
                </form>

                <p className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono text-center mt-6">
                    stored on-device · never shared
                </p>
            </div>
        </div>
    );
};

export default OnboardingDialog;
